import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bike, Store as StoreIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { brl, PAYMENT } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { PixKeyBox } from "@/components/store/PixKeyBox";

const field = "w-full rounded-xl border border-white/[0.08] bg-[#1A1A1A] px-4 py-3 text-sm outline-none focus:border-primo-500/60";
const CUSTOMER_KEY = "primo_customer";

const Choice = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${active ? "border-primo-500 bg-primo-500/10 text-primo-400" : "border-white/[0.08] bg-[#1A1A1A] text-slate-300"}`}>
    {children}
  </button>
);

export default function Checkout() {
  const { list, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState(() => ({
    customer_name: "", phone: "", address: "", notes: "", fulfillment: "entrega", payment_method: "pix", change_for: "",
    ...JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "{}"),
  }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  useEffect(() => {
    if (!coupon) return;
    api.post("/coupons/validate", { code: coupon.code, subtotal })
      .then((r) => setCoupon(r.data))
      .catch(() => { setCoupon(null); toast.error("O cupom não vale mais para este valor de pedido"); });
  }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const fee = form.fulfillment === "entrega" ? settings?.delivery_fee || 0 : 0;
  const minOrder = settings?.min_order_delivery || 0;
  const belowMin = form.fulfillment === "entrega" && subtotal < minOrder;
  const discount = coupon?.discount || 0;
  const total = Math.max(subtotal + fee - discount, 0);

  const applyCoupon = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: code.trim(), subtotal });
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado: -${brl(data.discount)}`);
    } catch (err) { setCoupon(null); toast.error(errMsg(err)); } finally { setChecking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        change_for: form.payment_method === "dinheiro" && form.change_for ? Number(form.change_for) : null,
        items: list.map((i) => ({ product_id: i.product.id, qty: i.qty, selections: i.selections })),
        coupon_code: coupon?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      const { customer_name, phone, address } = form;
      localStorage.setItem(CUSTOMER_KEY, JSON.stringify({ customer_name, phone, address }));
      clear();
      navigate(`/pedido/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (!list.length) {
    return (
      <div className="grid min-h-screen place-items-center px-4 text-center">
        <div><p className="text-slate-400">Seu carrinho está vazio.</p>
          <Link to="/" data-testid="back-to-store-link" className="mt-4 inline-block text-primo-400">Voltar à loja</Link></div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl px-4 pb-32 pt-5">
      <Link to="/" data-testid="checkout-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Continuar comprando</Link>
      <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight">Finalizar pedido</h1>

      <section className="mt-8 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Como quer receber?</p>
        <div className="grid grid-cols-2 gap-3">
          <Choice testid="fulfillment-entrega" active={form.fulfillment === "entrega"} onClick={() => set("fulfillment")("entrega")}><Bike className="h-4 w-4" /> Entrega · {brl(settings?.delivery_fee)}</Choice>
          <Choice testid="fulfillment-retirada" active={form.fulfillment === "retirada"} onClick={() => set("fulfillment")("retirada")}><StoreIcon className="h-4 w-4" /> Retirar no local</Choice>
        </div>
        {belowMin && (
          <p data-testid="min-order-warning" className="rounded-xl border border-primo-500/40 bg-primo-500/10 p-3 text-sm text-primo-300">
            Pedido mínimo para entrega: <span className="font-bold">{brl(minOrder)}</span>. Faltam {brl(minOrder - subtotal)} — ou escolha retirar no local.
          </p>
        )}
      </section>

      <section className="mt-8 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Seus dados</p>
        <input required minLength={2} data-testid="checkout-name-input" placeholder="Seu nome" value={form.customer_name} onChange={set("customer_name")} className={field} />
        <input required minLength={8} type="tel" data-testid="checkout-phone-input" placeholder="Telefone / WhatsApp" value={form.phone} onChange={set("phone")} className={field} />
        {form.fulfillment === "entrega" && (
          <input required minLength={5} data-testid="checkout-address-input" placeholder="Endereço completo (rua, número, bairro)" value={form.address} onChange={set("address")} className={field} />
        )}
        <textarea data-testid="checkout-notes-input" placeholder="Observações (opcional)" rows={2} value={form.notes} onChange={set("notes")} className={field} />
      </section>

      <section className="mt-8 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Pagamento na {form.fulfillment === "entrega" ? "entrega" : "retirada"}</p>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(PAYMENT).map(([k, label]) => (
            <Choice key={k} testid={`payment-${k}`} active={form.payment_method === k} onClick={() => set("payment_method")(k)}>{label.split(" ")[0]}</Choice>
          ))}
        </div>
        {form.payment_method === "dinheiro" && (
          <input type="number" min={0} step="0.01" data-testid="checkout-change-input" placeholder="Troco para quanto? (opcional)" value={form.change_for} onChange={set("change_for")} className={field} />
        )}
        {form.payment_method === "pix" && (
          <p data-testid="checkout-pix-notice" className="rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm font-semibold text-yellow-300">
            Favor enviar o comprovante de pagamento para validar sua compra
          </p>
        )}
        {form.payment_method === "pix" && <PixKeyBox pixKey={settings?.pix_key} testid="checkout-pix-key" />}
      </section>

      <section className="mt-8 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Cupom de desconto</p>
        <div className="flex gap-2">
          <input data-testid="coupon-input" placeholder="Digite seu cupom" value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setCoupon(null); }} className={`${field} uppercase`} />
          <button type="button" data-testid="coupon-apply-button" onClick={applyCoupon} disabled={checking || !code.trim()}
            className="shrink-0 rounded-xl bg-primo-500 px-5 text-sm font-bold text-white hover:bg-primo-400 disabled:opacity-40">
            {checking ? "..." : "Aplicar"}
          </button>
        </div>
        {coupon && (
          <p data-testid="coupon-applied" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-400">
            Cupom <span className="font-bold">{coupon.code}</span> aplicado{coupon.partner ? ` (parceria ${coupon.partner})` : ""} · -{brl(coupon.discount)}
          </p>
        )}
      </section>

      <section className="mt-8 rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5 space-y-2 text-sm">
        {list.map(({ key, product, qty, selections, unitPrice }) => (
          <div key={key}>
            <div className="flex justify-between gap-4"><span className="text-slate-300">{qty}x {product.name}</span><span>{brl(unitPrice * qty)}</span></div>
            {selections.length > 0 && <p className="text-xs text-slate-500">{selections.map((s) => `${s.qty}x ${s.option}${s.price ? ` (+${brl(s.price)})` : ""}`).join(", ")}</p>}
          </div>
        ))}
        <div className="border-t border-white/[0.08] pt-2 flex justify-between text-slate-400"><span>Subtotal</span><span>{brl(subtotal)}</span></div>
        <div className="flex justify-between text-slate-400"><span>Taxa de entrega</span><span data-testid="checkout-fee">{brl(fee)}</span></div>
        {discount > 0 && (
          <div className="flex justify-between text-emerald-400"><span>Desconto ({coupon.code})</span><span data-testid="checkout-discount">-{brl(discount)}</span></div>
        )}
        <div className="flex justify-between font-display text-xl font-bold"><span>Total</span><span data-testid="checkout-total" className="text-primo-400">{brl(total)}</span></div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-30 bg-gradient-to-t from-[#0A0A0A] p-4">
        <button type="submit" data-testid="checkout-submit-button" disabled={loading || !settings?.store_open || belowMin}
          className="mx-auto block w-full max-w-xl rounded-full bg-primo-500 py-4 font-display text-lg font-bold text-white hover:bg-primo-400 disabled:opacity-40">
          {!settings?.store_open ? "Loja fechada" : belowMin ? `Mínimo p/ entrega ${brl(minOrder)}` : loading ? "Enviando..." : `Enviar pedido · ${brl(total)}`}
        </button>
      </div>
    </form>
  );
}
